import { constants, type Stats } from "node:fs";
import {
  lstat,
  mkdir,
  open,
  readFile,
  rm,
} from "node:fs/promises";
import { homedir } from "node:os";
import { dirname, join, resolve } from "node:path";
import { Writable } from "node:stream";
import { createInterface } from "node:readline";

import { hashHistoryCode, normalizeHistoryCode } from "@agentvault/crypto";
import { writeHistoryRecoveryCodeFile } from "./history-recovery-file.mjs";
import { loadState } from "./state.js";

export interface StoredHistoryRecoveryCode {
  version: 1;
  tenantId: string;
  code: string;
}

const TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIR_MODE = 0o700;
const FILE_MODE = 0o600;
export const HISTORY_RECOVERY_VERIFY_TIMEOUT_MS = 10_000;
const VERIFY_TIMEOUT_MESSAGE = "Agent history recovery verification timed out";

class HistoryRecoveryVerificationTimeoutError extends Error {}

function canonicalTenantId(tenantId: string): string {
  if (!TENANT_ID_PATTERN.test(tenantId)) {
    throw new Error("Invalid tenant ID");
  }
  return tenantId.toLowerCase();
}

interface RecoveryPathLayout {
  /** Trusted boundary itself is not traversed or permission-checked. */
  boundary: string;
  /** Every component below the boundary is checked with lstat. */
  components: string[];
  directory: string;
}

function recoveryPathLayout(env: NodeJS.ProcessEnv): RecoveryPathLayout {
  // This override exists only for isolated tests. Production callers cannot
  // redirect a recovery secret through ambient process configuration. Its
  // parent is the trusted boundary so the override itself is still checked.
  if ((env.NODE_ENV === "test" || env.VITEST === "true") && env.AGENTVAULT_RECOVERY_DIR) {
    const directory = resolve(env.AGENTVAULT_RECOVERY_DIR);
    return {
      boundary: dirname(directory),
      components: [directory.slice(dirname(directory).length + 1)],
      directory,
    };
  }
  // HOME is the production trust boundary. We check each secret-bearing child
  // beneath it; system ancestors above HOME may legitimately be root-owned.
  const boundary = resolve(env.HOME || homedir());
  return {
    boundary,
    components: [".agentvault", "recovery", "history"],
    directory: join(boundary, ".agentvault", "recovery", "history"),
  };
}

export function historyRecoveryCodePath(
  tenantId: string,
  env: NodeJS.ProcessEnv = process.env,
): string {
  return join(recoveryPathLayout(env).directory, `${canonicalTenantId(tenantId)}.json`);
}

function isOwnerOnlyFile(stat: Stats): boolean {
  if (!stat.isFile() || stat.isSymbolicLink() || (stat.mode & 0o777) !== FILE_MODE) {
    return false;
  }
  return typeof process.getuid !== "function" || stat.uid === process.getuid();
}

function assertSecureDirectory(stat: Stats, final: boolean): void {
  const permissions = stat.mode & 0o777;
  const wrongOwner = typeof process.getuid === "function" && stat.uid !== process.getuid();
  const unsafePermissions =
    (permissions & 0o700) !== 0o700 ||
    (permissions & 0o022) !== 0 ||
    (final && permissions !== DIR_MODE);
  if (!stat.isDirectory() || stat.isSymbolicLink() || wrongOwner || unsafePermissions) {
    throw new Error("Recovery storage path is not secure");
  }
}

/**
 * Walk every component below the trusted HOME/override-parent boundary with
 * lstat. Never recursively mkdir through an unchecked ancestor.
 */
async function secureRecoveryDirectory(
  env: NodeJS.ProcessEnv,
  create: boolean,
): Promise<string | null> {
  const layout = recoveryPathLayout(env);
  let current = layout.boundary;
  for (let index = 0; index < layout.components.length; index += 1) {
    current = join(current, layout.components[index]);
    let stat: Stats;
    try {
      stat = await lstat(current);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        throw new Error("Recovery storage path is not secure");
      }
      if (!create) return null;
      try {
        await mkdir(current, { mode: DIR_MODE });
        stat = await lstat(current);
      } catch (createError) {
        // Another process may win the first-use mkdir race. EEXIST is safe only
        // after the winner's node is lstat-checked below like every other path.
        if ((createError as NodeJS.ErrnoException).code !== "EEXIST") {
          throw new Error("Recovery storage path is not secure");
        }
        try {
          stat = await lstat(current);
        } catch {
          throw new Error("Recovery storage path is not secure");
        }
      }
    }
    assertSecureDirectory(stat, index === layout.components.length - 1);
  }
  return layout.directory;
}

export async function loadHistoryRecoveryCode(
  tenantId: string,
  env: NodeJS.ProcessEnv = process.env,
): Promise<string | null> {
  const canonicalTenant = canonicalTenantId(tenantId);
  const filePath = historyRecoveryCodePath(canonicalTenant, env);
  let handle: Awaited<ReturnType<typeof open>> | undefined;
  try {
    if (!await secureRecoveryDirectory(env, false)) return null;
    handle = await open(filePath, constants.O_RDONLY | constants.O_NOFOLLOW);
    const stat = await handle.stat();
    if (!isOwnerOnlyFile(stat)) return null;

    const raw = await handle.readFile({ encoding: "utf8" });
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as Partial<StoredHistoryRecoveryCode>;
    if (record.version !== 1 || record.tenantId !== canonicalTenant || typeof record.code !== "string") {
      return null;
    }
    const normalized = normalizeHistoryCode(record.code);
    return normalized === record.code ? normalized : null;
  } catch {
    return null;
  } finally {
    await handle?.close().catch(() => undefined);
  }
}

export async function configureHistoryRecoveryCode(input: {
  tenantId: string;
  code: string;
  apiUrl: string;
  deviceJwt: string;
  env?: NodeJS.ProcessEnv;
}): Promise<void> {
  const env = input.env ?? process.env;
  const tenantId = canonicalTenantId(input.tenantId);
  const code = normalizeHistoryCode(input.code);
  const codeHash = hashHistoryCode(code);
  const dir = await secureRecoveryDirectory(env, true);
  if (!dir) throw new Error("Recovery storage path is not secure");

  const controller = new AbortController();
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutHandle = setTimeout(() => {
      controller.abort();
      reject(new HistoryRecoveryVerificationTimeoutError());
    }, HISTORY_RECOVERY_VERIFY_TIMEOUT_MS);
  });
  let response: Response;
  try {
    response = await Promise.race([
      fetch(
        `${input.apiUrl.replace(/\/+$/, "")}/api/v1/agent-history-recovery/verify`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${input.deviceJwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code_hash: codeHash }),
          signal: controller.signal,
        },
      ),
      timeout,
    ]);
  } catch (error) {
    if (error instanceof HistoryRecoveryVerificationTimeoutError) {
      throw new Error(VERIFY_TIMEOUT_MESSAGE);
    }
    if (timeoutHandle) clearTimeout(timeoutHandle);
    throw new Error("Unable to verify agent history recovery code");
  }

  if (!response.ok) {
    if (timeoutHandle) clearTimeout(timeoutHandle);
    throw new Error(`Unable to verify agent history recovery code (HTTP ${response.status})`);
  }
  let result: unknown;
  try {
    result = await Promise.race([response.json(), timeout]);
  } catch (error) {
    if (error instanceof HistoryRecoveryVerificationTimeoutError) {
      throw new Error(VERIFY_TIMEOUT_MESSAGE);
    }
    throw new Error("Agent history recovery verification returned an invalid response");
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }
  if (
    !result ||
    typeof result !== "object" ||
    (result as { configured?: unknown }).configured !== true ||
    (result as { matches?: unknown }).matches !== true
  ) {
    throw new Error("Agent history recovery code does not match the configured code");
  }

  try {
    await writeHistoryRecoveryCodeFile({ directory: dir, tenantId, code });
  } catch {
    throw new Error("Unable to store agent history recovery code securely");
  }
}

export async function clearHistoryRecoveryCode(
  tenantId: string,
  env: NodeJS.ProcessEnv = process.env,
): Promise<void> {
  const canonicalTenant = canonicalTenantId(tenantId);
  if (!await secureRecoveryDirectory(env, false)) return;
  const filePath = historyRecoveryCodePath(canonicalTenant, env);
  let handle: Awaited<ReturnType<typeof open>> | undefined;
  try {
    handle = await open(filePath, constants.O_RDONLY | constants.O_NOFOLLOW);
    if (!isOwnerOnlyFile(await handle.stat())) {
      throw new Error("Recovery code file is not secure");
    }
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return;
    if (error instanceof Error && error.message === "Recovery code file is not secure") throw error;
    throw new Error("Recovery code file is not secure");
  } finally {
    await handle?.close().catch(() => undefined);
  }
  await rm(filePath);
}

interface EnrolledRecoveryIdentity {
  tenantId: string;
  deviceJwt: string;
}

async function loadEnrolledRecoveryIdentity(dataDir: string): Promise<EnrolledRecoveryIdentity> {
  const state = await loadState(dataDir);
  const deviceJwt = state?.deviceJwt;
  if (typeof deviceJwt !== "string" || !deviceJwt) {
    throw new Error("No enrolled AgentVault device state was found");
  }
  try {
    const parts = deviceJwt.split(".");
    if (parts.length !== 3) throw new Error("malformed");
    const payload = JSON.parse(Buffer.from(parts[1], "base64url").toString("utf8"));
    if (payload?.type !== "device" || typeof payload?.tid !== "string") throw new Error("claims");
    return { tenantId: canonicalTenantId(payload.tid), deviceJwt };
  } catch {
    throw new Error("Enrolled AgentVault device state has an invalid identity");
  }
}

/** Read a line without echoing its contents. Returns null on EOF/non-TTY. */
export async function readMaskedHistoryRecoveryCode(
  prompt = "Agent history recovery code: ",
): Promise<string | null> {
  if (!process.stdin.isTTY || !process.stdout.isTTY) return null;

  let muted = false;
  const output = new Writable({
    write(chunk, encoding, callback) {
      if (!muted) process.stdout.write(chunk, encoding as BufferEncoding);
      callback();
    },
  });
  const rl = createInterface({ input: process.stdin, output, terminal: true });
  return new Promise((resolveValue) => {
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      muted = false;
      process.stdout.write("\n");
      rl.close();
      resolveValue(value);
    };
    rl.once("close", () => finish(null));
    rl.question(prompt, (answer) => finish(answer));
    muted = true;
  });
}

export interface HistoryRecoveryCommandInput {
  args: string[];
  dataDir: string;
  apiUrl: string;
  env?: NodeJS.ProcessEnv;
  output?: (message: string) => void;
  readCodeFile?: (path: string) => Promise<string>;
  readMaskedCode?: () => Promise<string | null>;
}

/** Execute the arguments that follow `agentvault recovery`. */
export async function runHistoryRecoveryCommand(input: HistoryRecoveryCommandInput): Promise<void> {
  const env = input.env ?? process.env;
  const output = input.output ?? console.log;
  const [action, ...rest] = input.args;
  if (Object.prototype.hasOwnProperty.call(env, "AGENTVAULT_HISTORY_RECOVERY_CODE")) {
    throw new Error("AGENTVAULT_HISTORY_RECOVERY_CODE is not accepted; use masked input or --code-file");
  }
  if (!action || !["configure", "status", "clear"].includes(action)) {
    throw new Error("Usage: agentvault recovery <configure|status|clear>");
  }

  const codeFlags = rest.filter((arg) => arg === "--code" || arg.startsWith("--code="));
  if (codeFlags.length > 0) {
    throw new Error("Raw recovery codes are not accepted in command arguments");
  }
  const codeFileArgs = rest.filter((arg) => arg.startsWith("--code-file="));
  const unknown = rest.filter((arg) => !arg.startsWith("--code-file="));
  if (unknown.length > 0) {
    throw new Error("Raw recovery codes and positional arguments are not accepted");
  }
  if (codeFileArgs.length > 1 || (action !== "configure" && codeFileArgs.length > 0)) {
    throw new Error("--code-file is accepted only once with recovery configure");
  }

  const identity = await loadEnrolledRecoveryIdentity(resolve(input.dataDir));
  if (action === "status") {
    const configured = await loadHistoryRecoveryCode(identity.tenantId, env);
    output(`Agent history recovery: ${configured ? "configured" : "not configured"}`);
    return;
  }
  if (action === "clear") {
    await clearHistoryRecoveryCode(identity.tenantId, env);
    output("Agent history recovery: cleared");
    return;
  }

  let code: string | null;
  if (codeFileArgs.length === 1) {
    const codeFile = codeFileArgs[0].slice("--code-file=".length);
    if (!codeFile) throw new Error("--code-file requires a path");
    output(`Reading agent history recovery code from ${codeFile}`);
    code = await (input.readCodeFile ?? ((path) => readFile(path, "utf8")))(codeFile);
  } else {
    code = await (input.readMaskedCode ?? readMaskedHistoryRecoveryCode)();
  }
  if (code === null) {
    throw new Error("A recovery code is required via masked input or --code-file");
  }

  await configureHistoryRecoveryCode({
    tenantId: identity.tenantId,
    code,
    apiUrl: input.apiUrl,
    deviceJwt: identity.deviceJwt,
    env,
  });
  output("Agent history recovery: configured");
}

export async function offerHistoryRecoveryConfiguration(input: {
  dataDir: string;
  apiUrl: string;
  env?: NodeJS.ProcessEnv;
  confirm?: () => Promise<boolean>;
  readMaskedCode?: () => Promise<string | null>;
  output?: (message: string) => void;
}): Promise<void> {
  const output = input.output ?? console.log;
  const confirm = input.confirm ?? (async () => false);
  let accepted = false;
  try {
    accepted = await confirm();
  } catch {
    // EOF is a normal decline during an otherwise successful setup.
  }
  if (!accepted) {
    output("Agent history recovery: not configured");
    return;
  }

  try {
    await runHistoryRecoveryCommand({
      args: ["configure"],
      dataDir: input.dataDir,
      apiUrl: input.apiUrl,
      env: input.env,
      output,
      readMaskedCode: input.readMaskedCode,
    });
  } catch (error) {
    output(`Agent history recovery setup skipped: ${error instanceof Error ? error.message : "configuration failed"}`);
    output("Agent history recovery: not configured");
  }
}
